import { c as _c } from "react/compiler-runtime";
import { AppAccessResponse, Application } from "@justhome/api-core";
import { formatMoney } from "@justhome/common";
import { Icon, Title } from "@justhome/react-ui";
import LeadWallForm, { LeadWallFormValue } from "@components/LeadWall/LeadWallForm";
import { GTM_EVENT_CONTACT_WALL } from "@lib/constants";
import gtm from "@lib/gtm";
import { useApplicationTranslation } from "@lib/hooks/translations";
import useLanguage from "@lib/hooks/useLanguage";
import useLinks from "@lib/hooks/useLinks";
import usePartnerContext from "@lib/hooks/usePartnerContext";
import { ApplicationFormValues, BuyWithSavingsFormValues } from "@lib/mapApplicationData/types";
import useApplicationContext from "@modules/application/hooks/useApplicationContext";
import usePostApplicationWithAppAccess from "@modules/application/hooks/usePostApplicationWithAppAccess";
import Image from "next/image";
import { useRouter } from "@lib/hooks/useRouter";
import { FC, useEffect } from "react";
import ApplicationLayout from "../ApplicationLayout";
import { FormStepComponent } from "../FormSteps/FormSteps";
const FEATURE_KEYS = ["app_access_feature_one_click", "app_access_feature_realtime_rates", "app_access_feature_advisor"];
const APP_OFFERS_IMAGE = "/assets/img/application/app-fade.png";
const AppAccessLeadWall: FC<FormStepComponent<ApplicationFormValues>> = t0 => {
  const $ = _c(80);
  const {
    value
  } = t0;
  const buyWithSavingsValues = value as Partial<BuyWithSavingsFormValues>;
  const {
    t
  } = useApplicationTranslation();
  const locale = useLanguage();
  const router = useRouter();
  const {
    dispatch,
    state
  } = useApplicationContext();
  const {
    partner
  } = usePartnerContext();
  const {
    getApplicationProcessLink
  } = useLinks();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp, t1);
  let t2;
  if ($[1] !== router) {
    t2 = () => {
      router.back();
    };
    $[1] = router;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleGoBack = t2;
  let t3;
  if ($[3] !== dispatch || $[4] !== getApplicationProcessLink || $[5] !== router) {
    t3 = (application, appAccess) => {
      dispatch({
        type: "SET_APPLICATION",
        application
      });
      dispatch({
        type: "SET_LEAD",
        lead: {
          id: appAccess.leadId,
          email: appAccess.email,
          disqualified: appAccess.disqualified,
          needsReconfirmation: appAccess.needsReconfirmation,
          isOffersViewForbidden: appAccess.isOffersViewForbidden
        }
      });
      router.push(getApplicationProcessLink("app-access", {
        emailSent: String(appAccess.emailSent)
      }));
    };
    $[3] = dispatch;
    $[4] = getApplicationProcessLink;
    $[5] = router;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleSuccess = t3;
  const t4 = value as ApplicationFormValues;
  let t5;
  if ($[7] !== handleSuccess || $[8] !== partner || $[9] !== t4) {
    t5 = {
      partner,
      applicationForm: t4,
      onSuccess: handleSuccess
    };
    $[7] = handleSuccess;
    $[8] = partner;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    isLoading,
    isError,
    handleSubmit
  } = usePostApplicationWithAppAccess(t5);
  let t6;
  if ($[11] !== t) {
    t6 = t("app_access_title");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <Title className="text-h1 font-book mb-3s">{t6}</Title>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t("app_access_description");
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <p className="mb-m text-body-1 text-neutral-325">{t8}</p>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t("app_access_image_alt");
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10) {
    t11 = <Image src={APP_OFFERS_IMAGE} alt={t10} width={3208} height={5600} quality={90} sizes="25vw" className=" flex-1 min-w-[1px] h-auto" />;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t) {
    t12 = FEATURE_KEYS.map(key => <li key={key} className="flex items-start gap-2xs"><div className="w-3s h-3s bg-primary-50 rounded-full flex-none flex items-center justify-center"><Icon type="CheckSmall" size="16" className="text-primary-800 flex-none" /></div><span><span className="block font-medium m:mb-xs">{t(`${key}_title`)}</span><span className="text-neutral-325 hidden m:block">{t(`${key}_description`)}</span></span></li>);
    $[23] = t;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12) {
    t13 = <ul className="text-body-2 space-y-2xs m:space-y-3s flex-1">{t12}</ul>;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t11 || $[28] !== t13) {
    t14 = <div className="items-center flex gap m:items-start">{t11}{t13}</div>;
    $[27] = t11;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14 || $[31] !== t7 || $[32] !== t9) {
    t15 = <div className="m:border-r border-neutral-50 m:pr-m l:pr-xl">{t7}{t9}{t14}</div>;
    $[30] = t14;
    $[31] = t7;
    $[32] = t9;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t) {
    t16 = t("app_access_summary_title");
    $[34] = t;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t16) {
    t17 = <p className="text-body-2 text-neutral-325 mb-xs">{t16}</p>;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== buyWithSavingsValues.postalCode || $[39] !== t) {
    t18 = t("app_access_summary_location", {
      postalCode: buyWithSavingsValues.postalCode
    });
    $[38] = buyWithSavingsValues.postalCode;
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== buyWithSavingsValues.downPayment || $[42] !== buyWithSavingsValues.purchasePrice || $[43] !== locale || $[44] !== t) {
    t19 = t("app_access_summary_figures", {
      purchasePrice: formatMoney(buyWithSavingsValues.purchasePrice, false, locale),
      downPayment: formatMoney(buyWithSavingsValues.downPayment, false, locale)
    });
    $[41] = buyWithSavingsValues.downPayment;
    $[42] = buyWithSavingsValues.purchasePrice;
    $[43] = locale;
    $[44] = t;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t18 || $[47] !== t19) {
    t20 = <p>{t18}{" \xB7 "}{t19}</p>;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t17 || $[50] !== t20) {
    t21 = <div className="rounded-s border border-primary-100 bg-primary-25 px-s py-3xs mb-3s m:mb-m">{t17}{t20}</div>;
    $[49] = t17;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== t) {
    t22 = t("app_access_form_title");
    $[52] = t;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== t22) {
    t23 = <Title className="text-h3 mb-2xs font-medium">{t22}</Title>;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t) {
    t24 = t("app_access_form_description");
    $[56] = t;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t24) {
    t25 = <p className="mb-2s text-body-2">{t24}</p>;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  const t26 = state.lead as Partial<LeadWallFormValue>;
  let t27;
  if ($[60] !== t) {
    t27 = t("app_access_form_submit");
    $[60] = t;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t) {
    t28 = t("app_access_form_email_hint");
    $[62] = t;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== handleGoBack || $[65] !== handleSubmit || $[66] !== isError || $[67] !== isLoading || $[68] !== t26 || $[69] !== t27 || $[70] !== t28) {
    t29 = <LeadWallForm isError={isError} isLoading={isLoading} onSubmit={handleSubmit} defaultValues={t26} onPrevious={handleGoBack} submitLabel={t27} emailHint={t28} />;
    $[64] = handleGoBack;
    $[65] = handleSubmit;
    $[66] = isError;
    $[67] = isLoading;
    $[68] = t26;
    $[69] = t27;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== t21 || $[73] !== t23 || $[74] !== t25 || $[75] !== t29) {
    t30 = <div className="m:pl-m l:pl-xl">{t21}{t23}{t25}{t29}</div>;
    $[72] = t21;
    $[73] = t23;
    $[74] = t25;
    $[75] = t29;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== t15 || $[78] !== t30) {
    t31 = <ApplicationLayout className="py-2s pb-xl m:pb-none "><div className="flex flex-col gap m:grid grid-cols-2">{t15}{t30}</div></ApplicationLayout>;
    $[77] = t15;
    $[78] = t30;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  return t31;
};
export default AppAccessLeadWall;
function _temp() {
  gtm.event(GTM_EVENT_CONTACT_WALL);
}